import { useQuery } from "@apollo/client";
import Box from "@mui/material/Box";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { GridRowHeightParams, GridRowParams, GridSortModel } from "@mui/x-data-grid-premium";
import { TAB_LINKS_HEIGHT } from "components/TabLinks";
import { ACTIVE_ORDER_STATUTE_CODES } from "encamp-shared/src/constants/echo";
import {
  HarmonizedViolationMatchProvenance,
  HarmonizedViolationProgram,
  HarmonizedViolationResolutionStatus,
  HarmonizedViolationSeverity,
  HarmonizedViolationsQuery,
} from "generated-graphql/graphql";
import { GridSelectionProvider, OmnisearchDataGridV2 } from "hooks/omnisearch-v2";
import { useOmnisearchActions, useOmnisearchStore } from "hooks/omnisearch-v2/omnisearchStore";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useCurrentUser } from "hooks/useCurrentUser";
import {
  HARMONIZED_VIOLATIONS,
  LATEST_HARMONIZED_VIOLATION_REFRESH,
} from "hooks/useHarmonizedViolations";
import { FilterKey } from "hooks/useOmnisearchDatagrid";
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  addOrUpdateKeyInOmnisearch,
  hasOmnisearchToken,
  HiddenDisplayToken,
  removeKeyInOmnisearch,
} from "utils/omnisearchUtils";

import {
  buildViolationColumns,
  descriptionCellHasChips,
  VIOLATION_DEFAULT_COLUMN_VISIBILITY,
  ViolationRow,
} from "./columns";
import { ViolationBulkActions } from "./ViolationBulkActions";
import { ViolationDetailDrawer } from "./ViolationDetailDrawer";
import {
  DATA_FRESHNESS_TOOLTIP,
  formatViolationDate,
  GOV_STATUS_LABEL,
  GOV_STATUS_OPTIONS,
  isMatchProvenance,
  MATCH_PROVENANCE_LABEL,
} from "./violationDisplay";
import { ViolationsOverviewPanel } from "./ViolationsOverviewPanel";

// The enum filters must be declared here (not derived from the columns): the
// column-derived filter path drops `enumValues`, so an enumAutocomplete built
// from a column would have no options. We therefore exclude every column except
// `facility` from the column-derived filters (below) and supply these instead.
// GOV_STATUS_LABEL keyed by raw string, so the filter's presentation fn resolves
// a label without an `as` cast.
const GOV_STATUS_LABEL_LOOKUP: Record<string, string | undefined> = GOV_STATUS_LABEL;

const VIOLATION_FILTER_KEYS: FilterKey[] = [
  { key: "violationId", header: "Violation ID", filterKeyType: "text" },
  { key: "state", header: "State", filterKeyType: "text" },
  { key: "pollutant", header: "Pollutant", filterKeyType: "text" },
  { key: "businessUnit", header: "Business unit", filterKeyType: "text" },
  { key: "region", header: "Region", filterKeyType: "text" },
  {
    key: "program",
    header: "Program",
    filterKeyType: "enumAutocomplete",
    enumValues: Object.values(HarmonizedViolationProgram),
  },
  {
    key: "severity",
    header: "Severity",
    filterKeyType: "enumAutocomplete",
    enumValues: Object.values(HarmonizedViolationSeverity),
  },
  {
    key: "status",
    header: "Reported status",
    filterKeyType: "enumAutocomplete",
    enumValues: Object.values(HarmonizedViolationResolutionStatus),
  },
  {
    // The user-tracked resolution workflow (`govStatus`), matching the grid's
    // "Resolution status" column. Distinct from EPA's "Reported status" above.
    key: "govStatus",
    header: "Resolution Status",
    filterKeyType: "enumAutocomplete",
    enumValues: [...GOV_STATUS_OPTIONS],
    // Record lookup keyed by raw string so a widened enum is a compile error in
    // GOV_STATUS_LABEL rather than a silent mislabel, with no cast here.
    enumPresentationFunction: (value) => GOV_STATUS_LABEL_LOOKUP[value] ?? value,
  },
  { key: "assignee", header: "Assignee", filterKeyType: "multipleUser" },
  {
    key: "provenance",
    header: "Responsible Party",
    filterKeyType: "enumAutocomplete",
    enumValues: Object.values(HarmonizedViolationMatchProvenance),
    // The Record lookup makes a widened enum a compile error there rather
    // than a silent mislabel here; the guard only discharges the string type.
    enumPresentationFunction: (value) =>
      isMatchProvenance(value) ? MATCH_PROVENANCE_LABEL[value] : value,
  },
  {
    key: "order",
    header: "EPA Order statute",
    filterKeyType: "enumAutocomplete",
    // `orderStatuteCode` is a plain string column (the statute set may grow),
    // so the options come from the shared constant that the harmonize task's
    // ORDER_STATUTES_BY_PROGRAM map `satisfies` — widening the map without
    // widening this dropdown is a compile error, not silent drift.
    enumValues: [...ACTIVE_ORDER_STATUTE_CODES],
    // Statute codes are acronyms; the default prettifier only knows some of
    // them and would render "Cwa"/"Caa" in the dropdown and filter chips.
    enumPresentationFunction: (value) => value,
  },
];

/**
 * Filter keys for the violations grid. Staff additionally get an "Encamp Assignee"
 * key backed by the cross-tenant staff user picker (`multipleStaffAnyUser`).
 * Customers never receive it — the backend ignores the omnisearch key for
 * non-staff anyway, but we also keep it out of their filter popover.
 */
export function buildViolationFilterKeys(isStaff: boolean): FilterKey[] {
  if (!isStaff) return VIOLATION_FILTER_KEYS;
  return [
    ...VIOLATION_FILTER_KEYS,
    { key: "staffAssignee", header: "Encamp Assignee", filterKeyType: "multipleStaffAnyUser" },
  ];
}

/** The omnisearch token the "Held pending confirmation" toggle owns. */
const heldToken = (): HiddenDisplayToken => ({ key: "held", value: "true" });

/** Keys the held toggle clears when switched on: any of them could hide a held
 *  row. Facility and assignee scoping are deliberately kept. */
export const HELD_CONFLICTING_KEYS = [
  "provenance",
  "status",
  "govStatus",
  "severity",
  "program",
  "pollutant",
  "order",
];

/**
 * Keys the held toggle keeps — they narrow which facilities, not which held rows.
 * With {@link HELD_CONFLICTING_KEYS} this must partition every staff filter key
 * (asserted in the test), so a new key forces a decision rather than defaulting.
 */
export const HELD_KEPT_KEYS = [
  "violationId",
  "state",
  "businessUnit",
  "region",
  "assignee",
  "staffAssignee",
];

/**
 * The omnisearch value applied by the default-filter-on-mount and the
 * "Unresolved only" toggle. Matches a `HarmonizedViolationResolutionStatus`.
 */
const UNRESOLVED_STATUS = HarmonizedViolationResolutionStatus.Unresolved;

/**
 * The tokens a toolbar control owns, as the single source for both that
 * control's active/toggle behavior and the grid's `hiddenDisplayTokens` list —
 * one definition, so a control can never highlight a token the input still
 * displays (or vice versa). `provenanceToken` (below) has no control; it's
 * hidden because the default-filter-on-mount always applies it.
 */
const unresolvedToken = (): HiddenDisplayToken => ({ key: "status", value: UNRESOLVED_STATUS });
// The user-tracked "Resolution status" (`govStatus`) the unresolved toggle also
// filters on, as a synthetic "not resolved by the user" value — govStatus has no
// UNRESOLVED member, so the backend maps this to "govStatus is not Resolved".
// Travels with the status token so "unresolved" means unresolved by BOTH EPA and
// the user; hidden from the input like the status token.
const GOV_UNRESOLVED = "UNRESOLVED";
const govUnresolvedToken = (): HiddenDisplayToken => ({ key: "govStatus", value: GOV_UNRESOLVED });
const assigneeToken = (email: string): HiddenDisplayToken => ({ key: "assignee", value: email });
const staffAssigneeToken = (email: string): HiddenDisplayToken => ({
  key: "staffAssignee",
  value: email,
});
// The default-filter-on-mount applies `provenance:DIRECT` (current-owner only).
// Hiding that exact token keeps the engineering term out of the search input;
// selecting "Former owner" via the filter popover writes a different value that
// stays visible as a pill.
const provenanceToken = (): HiddenDisplayToken => ({
  key: "provenance",
  value: HarmonizedViolationMatchProvenance.Direct,
});

/**
 * Tokens the grid hides from the search input's DISPLAY: the two default filters
 * (unresolved + current-owner provenance) plus the ONE assignee token the role's
 * "Assigned to me" toggle owns — staff hide `staffAssignee`, customers hide
 * `assignee`. The other role's assignee token is deliberately NOT hidden:
 * nothing on this screen toggles it, so a stray `assignee:<me>` on a staff URL
 * (old bookmark, saved preset, self-pick in the popover) stays visible as a pill
 * instead of filtering silently, matching the "unowned filter must stay visible"
 * rule the popover-set filters already follow.
 */
export function buildViolationHiddenDisplayTokens(
  email: string | undefined,
  isStaff: boolean
): HiddenDisplayToken[] {
  const tokens: HiddenDisplayToken[] = [unresolvedToken(), govUnresolvedToken(), provenanceToken()];
  if (email) {
    tokens.push(isStaff ? staffAssigneeToken(email) : assigneeToken(email));
  }
  // The held toggle reflects `held:true`, so keep the raw token out of the input.
  if (isStaff) {
    tokens.push(heldToken());
  }
  return tokens;
}

/**
 * Active state + toggle behavior for a toolbar control that owns one
 * omnisearch token. Active and hidden-from-display are decided by the same
 * predicate (`hasOmnisearchToken`) against the same token.
 */
function useOmnisearchTokenToggle(token: HiddenDisplayToken) {
  const { omnisearch } = useOmnisearchStore();
  const { editKeyValues, removeKey } = useOmnisearchActions();
  const active = hasOmnisearchToken(omnisearch, token);
  const toggle = () => (active ? removeKey(token.key) : editKeyValues([[token.key, token.value]]));
  return { active, toggle };
}

/**
 * Toolbar toggle that filters the grid to violations effectively assigned to the
 * current user. Role-aware: a given user only ever fills one of the two assignee
 * roles, so one toggle serves both — staff filter the STAFF assignee
 * (`staffAssignee:<email>`), customers the customer assignee
 * (`assignee:<email>`). Either value shape matches the corresponding filter
 * popover (emails); the backend resolves it to an in-tenant user and matches the
 * effective assignee (and ignores the staff key for non-staff). The token is
 * hidden from the search input's display since the toggle already shows state.
 */
export function AssignedToMeToggle({
  currentUserEmail,
  isStaff,
}: {
  currentUserEmail: string | undefined;
  isStaff: boolean;
}) {
  const email = currentUserEmail ?? "";
  const token = isStaff ? staffAssigneeToken(email) : assigneeToken(email);
  const { active, toggle } = useOmnisearchTokenToggle(token);
  if (!currentUserEmail) return null;
  return (
    <FormControlLabel
      sx={{ ml: 2, mr: 0, whiteSpace: "nowrap" }}
      control={<Switch size="small" checked={active} onChange={toggle} />}
      label={<Typography variant="body2">Assigned to me</Typography>}
    />
  );
}

/**
 * Toolbar toggle reflecting whether the grid is filtered to unresolved
 * violations only. "Unresolved" means unresolved by BOTH EPA
 * (`status:UNRESOLVED`) and the user's own Resolution status
 * (`govStatus:UNRESOLVED`), so a violation the user marked Resolved drops out
 * even while EPA still reports it open. Written in one shot to avoid a
 * chained-snapshot race; active state keys off the EPA status token.
 */
export function UnresolvedOnlyToggle() {
  const { omnisearch } = useOmnisearchStore();
  const { setOmnisearchImmediate } = useOmnisearchActions();
  const active = hasOmnisearchToken(omnisearch, unresolvedToken());
  const toggle = () => {
    if (active) {
      let next = removeKeyInOmnisearch(omnisearch, "status");
      next = removeKeyInOmnisearch(next, "govStatus");
      setOmnisearchImmediate(next);
      return;
    }
    let next = addOrUpdateKeyInOmnisearch(omnisearch, "status", UNRESOLVED_STATUS);
    next = addOrUpdateKeyInOmnisearch(next, "govStatus", GOV_UNRESOLVED);
    setOmnisearchImmediate(next);
  };
  return (
    <FormControlLabel
      sx={{ ml: 2, mr: 0, whiteSpace: "nowrap" }}
      control={<Switch size="small" checked={active} onChange={toggle} />}
      label={<Typography variant="body2">Show only unresolved</Typography>}
    />
  );
}

/**
 * STAFF-ONLY toolbar toggle isolating the held backlog, default off. Turning it on
 * also clears every filter that could hide a held row, keeping facility/assignee
 * scoping. Rewritten in one write to avoid a chained-snapshot race.
 */
export function HeldPendingConfirmationToggle({ isStaff }: { isStaff: boolean }) {
  const { omnisearch } = useOmnisearchStore();
  const { setOmnisearchImmediate } = useOmnisearchActions();
  const active = hasOmnisearchToken(omnisearch, heldToken());
  const toggle = () => {
    if (active) {
      // Restore the defaults the on-switch cleared, so turning off can't strand the
      // grid filter-less and silently showing former-owner rows.
      let next = removeKeyInOmnisearch(omnisearch, "held");
      next = addOrUpdateKeyInOmnisearch(next, "status", UNRESOLVED_STATUS);
      next = addOrUpdateKeyInOmnisearch(next, "govStatus", GOV_UNRESOLVED);
      next = addOrUpdateKeyInOmnisearch(
        next,
        "provenance",
        HarmonizedViolationMatchProvenance.Direct
      );
      setOmnisearchImmediate(next);
      return;
    }
    let next = omnisearch;
    for (const key of HELD_CONFLICTING_KEYS) {
      next = removeKeyInOmnisearch(next, key);
    }
    setOmnisearchImmediate(addOrUpdateKeyInOmnisearch(next, "held", "true"));
  };
  // STAFF-ONLY: self-gates rather than trusting every call site to omit it.
  if (!isStaff) {
    return null;
  }
  return (
    <FormControlLabel
      sx={{ ml: 2, mr: 0, whiteSpace: "nowrap" }}
      control={<Switch size="small" checked={active} onChange={toggle} />}
      label={<Typography variant="body2">Held pending confirmation</Typography>}
    />
  );
}

/**
 * Severity first — how users actually prioritize, and previously only implicit in
 * the API's no-sort fallback, so the grid showed no sort indicator at all.
 *
 * `asc` really is highest-first: Postgres orders enum values by declaration order
 * (HIGH, MEDIUM, LOW), not alphabetically. The resolver adds `lastSeenAt desc`
 * within a severity band.
 */
const VIOLATION_DEFAULT_SORT_MODEL: GridSortModel = [{ field: "severity", sort: "asc" }];

// `getRowHeight` MUST return an explicit number: the grid forwards only it to MUI
// (never `rowHeight`), so a null return falls through to MUI's 52px default.
const VIOLATION_ROW_HEIGHT = 56;
// Three-line rows; at most one chip renders, so this is enough headroom.
const VIOLATION_ROW_HEIGHT_WITH_CHIPS = 80;

/** Row height for a violation row: taller when its description cell shows a chip. */
export function violationRowHeight(
  row: Pick<ViolationRow, "matchProvenance" | "heldMatch">,
  isStaff: boolean
): number {
  return descriptionCellHasChips(row, isStaff)
    ? VIOLATION_ROW_HEIGHT_WITH_CHIPS
    : VIOLATION_ROW_HEIGHT;
}

const getItems = (data: HarmonizedViolationsQuery) => data.harmonizedViolations.items;
const getCount = (data: HarmonizedViolationsQuery) => data.harmonizedViolations.count;

/**
 * On first load with no omnisearch in the URL, default to unresolved-only and
 * direct-matches-only, so a fresh landing surfaces actionable violations and
 * prior-owner (shared-FRS) matches stay out of the clean view until asked for.
 * A bookmarked/explicit omnisearch param (even an empty toggle the user
 * cleared) is respected and never overridden.
 *
 * The `status` half is owned by the "Show only unresolved" toggle (which
 * reflects and clears it). The `provenance:DIRECT` half is intentional product
 * behavior with NO dedicated control: it is hidden from the search input (via
 * `provenanceToken` in `hiddenDisplayTokens`) so the engineering term never
 * shows, and a user surfaces former-owner rows by picking a different
 * "Responsible Party" value in the filter popover, which writes a visible pill.
 */
export function useDefaultViolationFilters() {
  const { omnisearch } = useOmnisearchStore();
  const { editKeyValues } = useOmnisearchActions();
  const [searchParams] = useSearchParams();
  const hasAppliedDefaultFilter = useRef(false);
  useEffect(() => {
    if (hasAppliedDefaultFilter.current) return;
    if (searchParams.has("omnisearch") || omnisearch) {
      hasAppliedDefaultFilter.current = true;
      return;
    }
    hasAppliedDefaultFilter.current = true;
    editKeyValues([
      ["status", UNRESOLVED_STATUS],
      ["govStatus", GOV_UNRESOLVED],
      ["provenance", HarmonizedViolationMatchProvenance.Direct],
    ]);
  }, [searchParams, omnisearch, editKeyValues]);
}

/**
 * Quiet top-right "when did this data last land" label, matching the waste
 * facility page's "Vendor data updated …". An alert banner overstated it — the
 * date is context, not a warning.
 */
export function DataFreshnessLabel({
  refresh,
}: {
  refresh: { startedAt?: string | null; finishedAt?: string | null } | null | undefined;
}) {
  const label = refresh
    ? `Violations data updated ${formatViolationDate(refresh.finishedAt ?? refresh.startedAt)}`
    : "Violations data has not been refreshed yet";
  return (
    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
      <Tooltip describeChild title={refresh ? DATA_FRESHNESS_TOOLTIP : ""}>
        <Typography variant="caption" fontStyle="italic" color="text.secondary">
          {label}
        </Typography>
      </Tooltip>
    </Box>
  );
}

export default function Violations() {
  const { tenantId } = useParams();
  const [selected, setSelected] = useState<ViolationRow | null>(null);
  const { user: currentUser, isStaff } = useCurrentUser();

  useBreadcrumb({ label: "Violations" });
  useDefaultViolationFilters();

  const { data: refreshData } = useQuery(LATEST_HARMONIZED_VIOLATION_REFRESH, {
    variables: { tenantId: tenantId ?? "" },
    skip: !tenantId,
  });
  const refresh = refreshData?.latestHarmonizedViolationRefresh;
  const refreshStartedAt = refresh?.startedAt ?? null;

  const columns = useMemo(
    () => buildViolationColumns(refreshStartedAt, isStaff),
    [refreshStartedAt, isStaff]
  );

  // Only `facility` is filtered via its column (a facility autocomplete).
  // program/severity/status come from VIOLATION_FILTER_KEYS; every other column
  // has no backend filter, so exclude them all from the filter popover.
  const excludeFilterColumns = useMemo(
    () => columns.filter((c) => c.field !== "facility").map((c) => c.field),
    [columns]
  );

  const buildVariables = useCallback(
    (omnisearch: string, pagination: { page: number; pageSize: number }, sort: GridSortModel) => ({
      tenantId: tenantId ?? "",
      search: omnisearch,
      page: pagination.page,
      pageSize: pagination.pageSize,
      sort: sort.map((s) => ({ field: s.field, sort: s.sort })),
    }),
    [tenantId]
  );

  const onRowClick = useCallback((params: GridRowParams<ViolationRow>) => {
    setSelected(params.row);
  }, []);

  // Rendered to the RIGHT of the omnisearch bar (DataGridToolbar places
  // `commandButtons` in the right section; `startCommandButtons` would be left).
  const commandButtons = useMemo<ReactNode[]>(
    () => [
      <ViolationBulkActions key="bulk-actions" tenantId={tenantId ?? ""} />,
      <AssignedToMeToggle
        key="assigned-to-me"
        currentUserEmail={currentUser?.email}
        isStaff={isStaff}
      />,
      <UnresolvedOnlyToggle key="unresolved-only" />,
      // STAFF-ONLY: the toggle self-gates, and the backend ignores `held:` too.
      <HeldPendingConfirmationToggle key="held-pending" isStaff={isStaff} />,
    ],
    [currentUser?.email, isStaff, tenantId]
  );

  const getRowHeight = useCallback(
    ({ model }: GridRowHeightParams) =>
      violationRowHeight(
        { matchProvenance: model.matchProvenance, heldMatch: model.heldMatch },
        isStaff
      ),
    [isStaff]
  );

  const filterKeys = useMemo(() => buildViolationFilterKeys(isStaff), [isStaff]);

  const hiddenDisplayTokens = useMemo<HiddenDisplayToken[]>(
    () => buildViolationHiddenDisplayTokens(currentUser?.email, isStaff),
    [currentUser?.email, isStaff]
  );

  return (
    <GridSelectionProvider>
      {/* A plain, body-scrolling page (like Shipment Documents / Waste Overview) —
          NOT a fixed-height flex column. The window is the scroll container, which
          is what lets the grid's `stickyToolbar` pin under the top nav; an
          internal-scroll layout (height:100% + flex:1) would leave sticky with no
          scrolling ancestor to react to. */}
      <Box
        sx={{
          // White page so the overview's light-gray metric cards read as distinct
          // containers rather than blending into a gray canvas.
          bgcolor: "background.paper",
        }}
      >
        <OmnisearchDataGridV2
          // Default horizontal padding (px:3) so the toolbar band's mx/px can
          // cancel it and bleed full-width. The sticky bar is the first thing
          // under the tabs; the freshness label and banner scroll beneath it.
          sx={{ pt: 0 }}
          // Pin the search bar + toggles under the top nav AND the sticky Insights
          // tab bar so they stay in view while the overview panel and rows scroll
          // beneath — offset by the tab bar's height so it doesn't pin behind it.
          stickyToolbar
          stickyToolbarTopOffset={TAB_LINKS_HEIGHT}
          // The freshness label and overview charts scroll beneath the sticky
          // toolbar; they read as page content here rather than floating alone
          // above the search bar. The charts respect the same omnisearch as the
          // table.
          betweenToolbarAndGrid={
            <Stack sx={{ my: 2 }} spacing={2}>
              <DataFreshnessLabel refresh={refresh} />
              <ViolationsOverviewPanel tenantId={tenantId ?? ""} />
            </Stack>
          }
          query={HARMONIZED_VIOLATIONS}
          getItems={getItems}
          getCount={getCount}
          columns={columns}
          buildVariables={buildVariables}
          onRowClick={onRowClick}
          checkboxSelection
          disableRowSelectionOnClick
          // Server-paginated: hide the header select-all checkbox so users check
          // rows explicitly rather than reaching for a "select all" that could only
          // ever cover the loaded page. This is a display hint — a keyboard select-all
          // still selects the loaded page — but selectedCount and getSelectedIds both
          // read the same loaded-page set, so the count and the write stay in agreement.
          disableSelectAll
          additionalFilterKeys={filterKeys}
          hiddenDisplayTokens={hiddenDisplayTokens}
          commandButtons={commandButtons}
          excludeFilterColumns={excludeFilterColumns}
          initialColumnVisibilityModel={VIOLATION_DEFAULT_COLUMN_VISIBILITY}
          initialSortModel={VIOLATION_DEFAULT_SORT_MODEL}
          // `rowHeight` is only the fallback when no `getRowHeight` is passed, so
          // it must match the two-line value.
          getRowHeight={getRowHeight}
          rowHeight={VIOLATION_ROW_HEIGHT}
          persistenceKey="harmonized-violations-grid"
          tenantId={tenantId}
          noRowsMessage="No violations have been recorded for this organization."
        />
        <ViolationDetailDrawer
          violation={selected}
          tenantId={tenantId ?? ""}
          refreshStartedAt={refreshStartedAt}
          onClose={() => setSelected(null)}
        />
      </Box>
    </GridSelectionProvider>
  );
}
